import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';

import { IErrorResponse } from 'common/models/api-service/error-response.model';
import { FileDetails, FieldError, SearchDTO, SearchVM } from 'common/models';
import { Mode } from '../enums/mode.enum';
import { ApiStatus, EntityType } from 'common/enums';
import {
    addCategories,
    addCategoriesByHierarchyId,
    collapseHierarchicalData,
    deselectRadioButton,
    findCategories,
    findCategoriesByHierarchyId,
    removeCategories,
    setCheckbox,
    setRadioButton,
    setSelectAllCheckbox,
    toggleExpansion,
    updateCategory,
    updateSubCategoryByHierarchyId
} from 'common/utils/table';

import initialMasterDataState from './masterData.state';
import {
    CategoryDTO,
    EntityVM,
    MasterDataEntity,
    PreviewEntityDTO
} from 'common/models/data/entity.model';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { EditSubCategoryDTO, LinkIndicatorDTO, SubCategoryDTO } from '../models/master-data.model';
import { IndicatorAddDTO, IndicatorUpdateDTO } from 'common/models/data/indicator.model';
import { ApiInitiator } from 'common/enums/data.enum';
import { IndicatorSourceDTO } from 'modules/configuration/models/configuration.model';
import { ExportRequestDto } from '../models/export.model';

const masterDataSlice = createSlice({
    name: 'masterData',
    initialState: initialMasterDataState,
    reducers: {
        uploadFile: (state, { payload }: PayloadAction<Array<FileDetails>>) => {
            if (!state.uploadStatus) {
                state.uploadStatus = {};
            }
            state.uploadingFiles = payload;
            state.selectedFiles = [];
            state.uploadStatus.status = ApiStatus.LOADING;
        },
        uploadFileSuccess: (state) => {
            state.uploadStatus.status = ApiStatus.SUCCESS;
            state.uploadingFiles = [];
            state.isUploadModalVisible = false;
            state.mode = Mode.PreviewGeneration;
        },
        uploadFileFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (state.uploadStatus) {
                state.uploadStatus.status = ApiStatus.ERROR;
                state.uploadStatus.error = payload;
                state.uploadingFiles = [];
            }
        },
        setSelectedFiles: (state, { payload }: PayloadAction<Array<FileDetails>>) => {
            state.selectedFiles = payload;
        },
        setUploadingFiles: (state, { payload }: PayloadAction<Array<FileDetails>>) => {
            state.uploadingFiles = payload;
        },
        toggleUploadModal: (state) => {
            state.isUploadModalVisible = !state.isUploadModalVisible;
        },
        setMode: (state, { payload }: PayloadAction<string>) => {
            state.mode = payload;
        },
        getMasterData: (state, { payload }: PayloadAction<PagedRequest>) => {
            state.masterData.status = ApiStatus.LOADING;
        },
        getMasterDataSuccess: (state, { payload }: PayloadAction<any>) => {
            state.masterData.status = ApiStatus.SUCCESS;
            if (payload.archivedDataList.pagination.currentPage === 1) {
                state.masterData.data = payload.archivedDataList;
            } else {
                if (
                    state.masterData.data &&
                    state.masterData.data.data &&
                    state.masterData.data.data.length > 0 &&
                    payload.archivedDataList &&
                    payload.archivedDataList.data.length > 0
                ) {
                    let updatedData = [...current(state.masterData.data.data)];
                    updatedData = updatedData.concat([...payload.archivedDataList.data]);
                    state.masterData.data.data = updatedData;
                    state.masterData.data.pagination = payload.archivedDataList.pagination;
                }
            }
        },
        getMasterDataError: (state, { payload }: PayloadAction<any>) => {
            state.masterData.status = ApiStatus.ERROR;
            state.masterData.error = payload.errors;
        },
        toggleExpansion: (state, { payload }: PayloadAction<EntityVM>) => {
            if (state.masterData.data?.data)
                state.masterData.data.data = toggleExpansion(
                    payload,
                    state.masterData.data?.data,
                    ApiInitiator.MasterData
                );
        },
        toggleExpansionSuccess: (
            state,
            { payload }: PayloadAction<{ parent: EntityVM; subCategories: EntityVM[] }>
        ) => {
            if (payload.subCategories && payload.subCategories.length > 0) {
                payload.subCategories.forEach(
                    (sc: EntityVM) => (sc.parentHierarchyId = payload.parent.hierarchyId)
                );
            }
            let entity: any = {};
            if (payload.parent.entityType === EntityType.Category)
                entity = findCategories(state.masterData.data?.data, payload.parent.id);
            else
                entity = findCategoriesByHierarchyId(
                    state.masterData.data?.data,
                    payload.parent.hierarchyId
                );
            if (
                entity.subCategories === undefined ||
                (entity.subCategories && entity.subCategories.length === 0)
            ) {
                if (
                    state.masterData.data?.data &&
                    payload.parent.entityType === EntityType.Category
                ) {
                    state.masterData.data.data = addCategories(
                        state.masterData.data?.data,
                        payload.parent.id,
                        payload.subCategories
                    );
                } else if (state.masterData.data?.data) {
                    state.masterData.data.data = addCategoriesByHierarchyId(
                        state.masterData.data?.data,
                        payload.parent.hierarchyId,
                        payload.subCategories
                    );
                }
            }
        },
        toggleExpansionFailed: (state, { payload }: PayloadAction<PreviewEntityDTO>) => {
            state.masterData.status = ApiStatus.ERROR;
        },
        refreshEntitiesByCategory: (
            state,
            { payload }: PayloadAction<{ entityType: EntityType; entityId: string }>
        ) => {
            state.masterData = { ...state.masterData, status: ApiStatus.LOADING };
        },
        refreshEntitiesByCategorySuccess: (
            state,
            {
                payload
            }: PayloadAction<{
                parent: { entityType: EntityType; entityId: string };
                subCategories: EntityVM[];
            }>
        ) => {
            let entity: any = {};
            if (payload.parent.entityType === EntityType.Category)
                entity = findCategories(state.masterData.data?.data, payload.parent.entityId);
            else
                entity = findCategoriesByHierarchyId(
                    state.masterData.data?.data,
                    payload.parent.entityId
                );
            if (entity) {
                entity.subCategories = [];
                if (entity.hasChild && payload.subCategories.length === 0) entity.hasChild = false;
                if (!entity.hasChild && payload.subCategories.length > 0) entity.hasChild = true;
                if (state.masterData.data?.data) {
                    if (payload.parent.entityType === EntityType.Category) {
                        state.masterData.data.data = updateCategory(
                            state.masterData.data.data,
                            entity.id,
                            entity
                        );
                        state.masterData.data.data = addCategories(
                            state.masterData.data?.data,
                            entity.id,
                            payload.subCategories
                        );
                    } else {
                        state.masterData.data.data = updateSubCategoryByHierarchyId(
                            state.masterData.data.data,
                            entity.hierarchyId,
                            entity
                        );

                        if (payload.subCategories && payload.subCategories.length > 0) {
                            payload.subCategories.forEach(
                                (sc: EntityVM) => (sc.parentHierarchyId = entity.hierarchyId)
                            );
                        }
                        state.masterData.data.data = addCategoriesByHierarchyId(
                            state.masterData.data?.data,
                            entity.hierarchyId,
                            payload.subCategories
                        );
                    }
                }
            }
        },
        refreshEntitiesByCategoryFailed: (state, { payload }: PayloadAction<PreviewEntityDTO>) => {
            state.masterData.status = ApiStatus.ERROR;
        },
        setFieldErrors: (state, { payload }: PayloadAction<Array<FieldError>>) => {
            state.fieldErrors = payload;
        },
        deleteData: (state, { payload }: PayloadAction<EntityVM>) => {
            state.deleteData.status = ApiStatus.LOADING;
        },
        deleteDataSuccess: (state, { payload }: PayloadAction<any>) => {
            state.deleteData.status = ApiStatus.SUCCESS;
        },
        deleteCategoryData: (state, { payload }: PayloadAction<string>) => {
            if (state.masterData.data?.data)
                state.masterData.data.data = removeCategories(state.masterData.data?.data, payload);
        },
        deleteDataError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.deleteData.status = ApiStatus.ERROR;
            state.deleteData.error = payload;
        },
        searchList: (state, { payload }: PayloadAction<SearchDTO>) => {
            state.searchList.status = ApiStatus.LOADING;
        },
        searchListSuccess: (
            state,
            { payload }: PayloadAction<PaginatedApiResponse<SearchVM[]>>
        ) => {
            state.searchList.status = ApiStatus.SUCCESS;
            if (
                state.searchList.data &&
                state.searchList.data?.data &&
                state.searchList.data.data.length > 0
            ) {
                if (payload.pagination.currentPage === 1) {
                    state.searchList.data.data = payload.data;
                } else {
                    if (payload.data) state.searchList.data?.data.push(...payload.data);
                }
                state.searchList.data.pagination = payload.pagination;
            } else {
                state.searchList.data = payload;
            }
        },
        searchListError: (state) => {
            state.searchList.status = ApiStatus.ERROR;
        },
        resetState: (state) => {
            state.editSubCategory = {};
            state.linkIndicator = {};
        },
        resetToInitialState: () => {
            return initialMasterDataState;
        },
        updateCategory: (state, { payload }: PayloadAction<CategoryDTO>) => {
            state.editStatus.status = ApiStatus.LOADING;
        },
        updateCategorySuccess: (state, { payload }: PayloadAction<CategoryDTO>) => {
            state.editStatus.status = ApiStatus.SUCCESS;
            state.isEditModalVisible = false;
            if (
                state.searchList.data &&
                state.searchList.data?.data &&
                state.searchList.data.data.length > 0
            ) {
                const categoryIndex = state.searchList.data.data.findIndex(
                    (sl: SearchVM) => sl.id === payload.categoryId
                );
                state.searchList.data.data[categoryIndex].name = payload.categoryName;
                state.searchList.data.data[categoryIndex].description = payload.description;
            }
        },
        updateCategoryFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.editStatus.status = ApiStatus.ERROR;
            state.editStatus.error = payload;
            state.isEditModalVisible = false;
        },
        fetchSubCategory: (state, { payload }: PayloadAction<string>) => {
            state.editSubCategory.status = ApiStatus.LOADING;
        },
        fetchSubCategorySuccess: (state, { payload }: PayloadAction<SubCategoryDTO>) => {
            state.editSubCategory.status = ApiStatus.SUCCESS;
            state.editSubCategory.data = payload;
        },
        fetchSubCategoryFailed: (state, { payload }: PayloadAction<string>) => {
            state.editSubCategory.status = ApiStatus.ERROR;
        },
        updateSubCategory: (state, { payload }: PayloadAction<EditSubCategoryDTO>) => {
            state.editStatus.status = ApiStatus.LOADING;
        },
        updateSubCategorySuccess: (state, { payload }: PayloadAction<EditSubCategoryDTO>) => {
            state.editStatus.status = ApiStatus.SUCCESS;
            state.isEditModalVisible = false;
            if (
                state.searchList.data &&
                state.searchList.data?.data &&
                state.searchList.data.data.length > 0
            ) {
                const categoryIndex = state.searchList.data.data.findIndex(
                    (sl: SearchVM) => sl.id === payload.subCategoryId
                );
                state.searchList.data.data[categoryIndex].name = payload.subCategoryName;
                state.searchList.data.data[categoryIndex].description = payload.description;
            }
        },
        updateSubCategoryFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.editStatus.status = ApiStatus.ERROR;
            state.editStatus.error = payload;
            state.isEditModalVisible = false;
        },
        updateIndicator: (
            state,
            {
                payload
            }: PayloadAction<{ editedIndicator: IndicatorUpdateDTO; indicator?: EntityVM }>
        ) => {
            state.editStatus.status = ApiStatus.LOADING;
        },
        updateIndicatorSuccess: (state, { payload }: PayloadAction<IndicatorUpdateDTO>) => {
            state.editStatus.status = ApiStatus.SUCCESS;
            state.isEditModalVisible = false;
            if (
                state.searchList.data &&
                state.searchList.data?.data &&
                state.searchList.data.data.length > 0
            ) {
                const categoryIndex = state.searchList.data.data.findIndex(
                    (sl: SearchVM) => sl.id === payload.indicatorId
                );
                state.searchList.data.data[categoryIndex].name = payload.name ? payload.name : '';
                state.searchList.data.data[categoryIndex].description = payload.description;
            }
        },
        updateIndicatorFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.editStatus.status = ApiStatus.ERROR;
            state.editStatus.error = payload;
            state.isEditModalVisible = false;
        },
        setEditModalVisibility: (state, { payload }: PayloadAction<any>) => {
            state.isEditModalVisible = payload;
        },
        addCategory: (state, { payload }: PayloadAction<CategoryDTO>) => {
            state.addedData.status = ApiStatus.LOADING;
        },
        addCategorySuccess: (state, { payload }: PayloadAction<CategoryDTO>) => {
            state.addedData.status = ApiStatus.SUCCESS;
            state.isAddModalVisible = false;
        },
        addCategoryFailed: (state, { payload }) => {
            state.addedData.status = ApiStatus.ERROR;
            state.addedData.error = payload;
            state.addedData.data = undefined;
            state.mode = Mode.Initial;
        },
        addSubCategory: (state, { payload }: PayloadAction<SubCategoryDTO>) => {
            state.addSubCategory.status = ApiStatus.LOADING;
        },
        addSubCategorySuccess: (state, { payload }) => {
            state.addSubCategory.status = ApiStatus.SUCCESS;
        },
        addSubCategoryFailed: (state, { payload }) => {
            state.addSubCategory.status = ApiStatus.ERROR;
        },
        setAddModalVisibility: (state, { payload }: PayloadAction<boolean>) => {
            state.isAddModalVisible = payload;
        },
        saveToMaster: (state) => {
            state.saveToMaster.status = ApiStatus.LOADING;
        },
        saveToMasterSuccess: (state) => {
            state.saveToMaster.status = ApiStatus.SUCCESS;
            state.mode = Mode.DataSavedToMaster;
        },
        saveToMasterFailed: (state) => {
            state.saveToMaster.status = ApiStatus.ERROR;
        },
        applyChanges: (state, { payload }) => {
            return state;
        },
        applyChangesSuccess: (state, { payload }) => {
            return state;
        },
        applyChangesFailed: (state, { payload }) => {
            return state;
        },
        setMessageBanner: (state, { payload }: PayloadAction<any>) => {
            state.messageBanner.isVisible = payload.isVisible;
            state.messageBanner.message = payload.message;
        },
        addIndicator: (state, { payload }: PayloadAction<IndicatorAddDTO>) => {
            state.addedData.status = ApiStatus.LOADING;
            state.addedData.data = payload;
        },
        addIndicatorSuccess: (state, { payload }: PayloadAction<any>) => {
            state.addedData.status = ApiStatus.SUCCESS;
        },
        addIndicatorFailed: (state, { payload }) => {
            state.addedData.status = ApiStatus.ERROR;
            state.addedData.error = payload;
            state.addedData.data = undefined;
        },
        linkIndicator: (state, { payload }: PayloadAction<LinkIndicatorDTO>) => {
            state.linkIndicator.status = ApiStatus.LOADING;
        },
        linkIndicatorSuccess: (state, { payload }: PayloadAction<any>) => {
            state.linkIndicator.status = ApiStatus.SUCCESS;
        },
        linkIndicatorFailed: (state, { payload }) => {
            state.linkIndicator.status = ApiStatus.ERROR;
        },
        setHierarchicalData: (state) => {
            state.hierarchicalData.status = ApiStatus.SUCCESS;
            state.hierarchicalData = { ...state.masterData };
        },
        getHierarchicalData: (state, { payload }: PayloadAction<PagedRequest>) => {
            state.hierarchicalData.status = ApiStatus.LOADING;
        },
        getHierarchicalDataSuccess: (state, { payload }: PayloadAction<any>) => {
            state.hierarchicalData.status = ApiStatus.SUCCESS;
            if (payload.archivedDataList.pagination.currentPage === 1) {
                state.hierarchicalData.data = payload.archivedDataList;
            } else {
                if (
                    state.hierarchicalData.data &&
                    state.hierarchicalData.data.data &&
                    state.hierarchicalData.data.data.length > 0 &&
                    payload.data &&
                    payload.data.length > 0
                ) {
                    let updatedData = [...current(state.hierarchicalData.data.data)];
                    updatedData = updatedData.concat([...payload.data]);
                    state.hierarchicalData.data.data = updatedData;
                    state.hierarchicalData.data.pagination = payload.archivedDataList.pagination;
                }
            }
        },
        getHierarchicalDataError: (state, { payload }: PayloadAction<any>) => {
            state.hierarchicalData.status = ApiStatus.ERROR;
            state.hierarchicalData.error = payload.errors;
        },
        toggleHierarchicalDataExpansion: (state, { payload }: PayloadAction<EntityVM>) => {
            if (state.hierarchicalData.data?.data)
                state.hierarchicalData.data.data = toggleExpansion(
                    payload,
                    state.hierarchicalData.data?.data,
                    ApiInitiator.ArchiveList
                );
        },
        toggleHierarchicalDataExpansionSuccess: (
            state,
            { payload }: PayloadAction<{ parent: EntityVM; subCategories: EntityVM[] }>
        ) => {
            const entity = findCategories(state.hierarchicalData.data?.data, payload.parent.id);
            if (
                entity.subCategories === undefined ||
                (entity.subCategories && entity.subCategories.length === 0)
            ) {
                if (state.hierarchicalData.data?.data) {
                    state.hierarchicalData.data.data = addCategories(
                        state.hierarchicalData.data?.data,
                        payload.parent.id,
                        payload.subCategories
                    );
                }
            }
        },
        toggleHierarchicalDataExpansionFailed: (
            state,
            { payload }: PayloadAction<PreviewEntityDTO>
        ) => {
            state.masterData.status = ApiStatus.ERROR;
        },
        setRadioButtonSelected: (state, { payload }: PayloadAction<any>) => {
            if (
                state.hierarchicalData.data &&
                state.hierarchicalData.data.data &&
                state.hierarchicalData.data?.data.length > 0
            ) {
                state.hierarchicalData.data.data = setRadioButton(
                    state.hierarchicalData.data.data,
                    payload.targetId,
                    payload.field
                );
            }
        },
        deselectRadioButton: (state) => {
            if (
                state.hierarchicalData.data &&
                state.hierarchicalData.data.data &&
                state.hierarchicalData.data.data.length > 0
            ) {
                state.hierarchicalData.data.data = deselectRadioButton(
                    state.hierarchicalData.data.data
                );
            }
        },
        setCheckboxSelected: (state, { payload }: PayloadAction<any>) => {
            if (
                state.hierarchicalData.data &&
                state.hierarchicalData.data.data &&
                state.hierarchicalData.data.data.length > 0
            ) {
                state.hierarchicalData.data.data = setCheckbox(
                    state.hierarchicalData.data.data,
                    payload.data.id
                );
            }
        },
        gatherSelectedVariants: (state, { payload }: PayloadAction<any>) => {
            const tempArray = [...state.selectedVariantsArray];
            if (payload.isSelected) {
                tempArray.push(payload.data);
            } else {
                const index = tempArray.indexOf(payload.data);
                tempArray.splice(index, 1);
            }
            state.selectedVariantsArray = tempArray;
        },
        resetSelectedVariants: (state) => {
            state.selectedVariantsArray = [];
        },
        setCheckboxSelectAll: (state, { payload }: PayloadAction<any>) => {
            if (
                state.hierarchicalData.data &&
                state.hierarchicalData.data.data &&
                state.hierarchicalData.data.data.length > 0
            ) {
                state.hierarchicalData.data.data = setSelectAllCheckbox(
                    state.hierarchicalData.data.data,
                    payload.isSelected
                );
            }
        },
        archive: (state, { payload }: PayloadAction<EntityVM>) => {
            state.archiveStatus.status = ApiStatus.LOADING;
        },
        archiveSuccess: (state, { payload }: PayloadAction<string>) => {
            state.archiveStatus.status = ApiStatus.SUCCESS;
            if (state.masterData.data?.data)
                state.masterData.data.data = removeCategories(state.masterData.data?.data, payload);
        },
        archiveFailed: (state) => {
            state.archiveStatus.status = ApiStatus.ERROR;
        },
        cloneEntity: (state, { payload }: PayloadAction<any>) => {
            return state;
        },
        cloneEntitySuccess: (state) => {
            return state;
        },
        cloneEntityFailed: (state) => {
            return state;
        },
        fetchReviewStatus: (state) => {
            state.reviewStatus.status = ApiStatus.LOADING;
        },
        fetchReviewStatusSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.reviewStatus.status = ApiStatus.SUCCESS;
            state.reviewStatus.data = payload;
        },
        fetchReviewStatusFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.reviewStatus.status = ApiStatus.ERROR;
            state.reviewStatus.error = payload;
        },
        moveSubCategoryBegin: (state, { payload }: PayloadAction<any>) => {
            state.moveSubCategory.status = ApiStatus.LOADING;
        },
        moveSubCategorySuccess: (state, { payload }: PayloadAction<any>) => {
            state.moveSubCategory.status = ApiStatus.SUCCESS;
            state.moveSubCategory.data = payload;
        },
        moveSubCategoryError: (state, { payload }: PayloadAction<any>) => {
            state.moveSubCategory.status = ApiStatus.ERROR;
            state.moveSubCategory.error = payload;
        },
        copyIndicator: (state, { payload }: PayloadAction<any>) => {
            state.copyIndicator.status = ApiStatus.LOADING;
        },
        copyIndicatorSuccess: (state, { payload }: PayloadAction<any>) => {
            state.copyIndicator.status = ApiStatus.SUCCESS;
        },
        copyIndicatorFailed: (state, { payload }) => {
            state.copyIndicator.status = ApiStatus.ERROR;
        },
        resetIndicatorCopy: (state) => {
            state.copyIndicator = {};
        },
        moveIndicatorBegin: (state, { payload }: PayloadAction<any>) => {
            state.moveIndicator.status = ApiStatus.LOADING;
        },
        moveIndicatorSuccess: (state, { payload }: PayloadAction<any>) => {
            state.moveIndicator.status = ApiStatus.SUCCESS;
            state.moveIndicator.data = payload;
        },
        moveIndicatorError: (state, { payload }: PayloadAction<any>) => {
            state.moveIndicator.status = ApiStatus.ERROR;
            state.moveIndicator.error = payload;
        },
        resetSearchResult: (state) => {
            state.searchList = {};
        },
        setScrollToTop: (state, { payload }: PayloadAction<boolean>) => {
            state.gridScrollToTop = payload;
        },
        getDeleteFeasibility: (state, { payload }: PayloadAction<any>) => {
            state.deleteFeasibility.status = ApiStatus.LOADING;
        },
        getDeleteFeasibilitySuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.deleteFeasibility.status = ApiStatus.SUCCESS;
            state.deleteFeasibility.data = payload;
        },
        getDeleteFeasibilityFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.deleteFeasibility.status = ApiStatus.ERROR;
            state.deleteFeasibility.error = payload;
        },
        getArchiveFeasibility: (
            state,
            { payload }: PayloadAction<{ id: string; hierarchyId: string; entityType: EntityType }>
        ) => {
            state.archiveFeasibility.status = ApiStatus.LOADING;
        },
        getArchiveFeasibilitySuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.archiveFeasibility.status = ApiStatus.SUCCESS;
            state.archiveFeasibility.data = payload;
        },
        getArchiveFeasibilityFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.archiveFeasibility.status = ApiStatus.ERROR;
            state.archiveFeasibility.error = payload;
        },
        resetDeleteFeasibility: (state) => {
            state.deleteFeasibility = {};
        },
        resetArchiveFeasibility: (state) => {
            state.archiveFeasibility = {};
        },
        resetReviewStatus: (state) => {
            state.reviewStatus = {};
        },
        getChildSubCategoryDataBegin: (state, { payload }: PayloadAction<any>) => {
            state.childSubCategoryData.status = ApiStatus.LOADING;
        },
        getChildSubCategoryDataSuccess: (state, { payload }: PayloadAction<any>) => {
            state.childSubCategoryData.status = ApiStatus.SUCCESS;
            state.childSubCategoryData.data = payload;
        },
        getChildSubCategoryDataFailed: (state, { payload }: PayloadAction<any>) => {
            state.childSubCategoryData.status = ApiStatus.ERROR;
            state.childSubCategoryData.error = payload;
        },
        isMovableSubCategoryBegin: (state, { payload }: PayloadAction<any>) => {
            state.isMovableSubCategory.status = ApiStatus.LOADING;
        },
        isMovableSubCategorySuccess: (state, { payload }: PayloadAction<any>) => {
            state.isMovableSubCategory.status = ApiStatus.SUCCESS;
            state.isMovableSubCategory.data = payload;
        },
        isMovableSubCategoryError: (state, { payload }: PayloadAction<any>) => {
            state.isMovableSubCategory.status = ApiStatus.ERROR;
            state.isMovableSubCategory.data = payload;
        },
        discardChanges: (state) => {
            state.discardChanges.status = ApiStatus.LOADING;
        },
        discardChangesSuccess: (state) => {
            state.discardChanges.status = ApiStatus.SUCCESS;
        },
        discardChangesFailed: (state) => {
            state.discardChanges.status = ApiStatus.ERROR;
        },
        collapseHierarchicalData: (state) => {
            if (state.hierarchicalData.data?.data)
                state.hierarchicalData.data.data = collapseHierarchicalData(
                    state.hierarchicalData.data?.data
                );
        },
        getCloneFeasibility: (
            state,
            { payload }: PayloadAction<{ id: string; entityType: EntityType }>
        ) => {
            state.cloneFeasibility.status = ApiStatus.LOADING;
        },
        getCloneFeasibilitySuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.cloneFeasibility.status = ApiStatus.SUCCESS;
            state.cloneFeasibility.data = payload;
        },
        getCloneFeasibilityFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.cloneFeasibility.status = ApiStatus.ERROR;
            state.cloneFeasibility.error = payload;
        },
        resetCloneFeasibility: (state) => {
            state.cloneFeasibility = {};
        },
        checkDuplicateIndicator: (state, { payload }: PayloadAction<string>) => {
            state.isDuplicateIndicator.status = ApiStatus.LOADING;
        },
        checkDuplicateIndicatorSuccess: (state, { payload }: PayloadAction<any>) => {
            state.isDuplicateIndicator.status = ApiStatus.SUCCESS;
            state.isDuplicateIndicator.data = payload;
        },
        checkDuplicateIndicatorFailed: (state, { payload }: PayloadAction<any>) => {
            state.isDuplicateIndicator.status = ApiStatus.ERROR;
            state.cloneFeasibility.error = payload;
        },
        resetIsDuplicateIndicator: (state) => {
            state.isDuplicateIndicator = {};
        }
    }
});

export default masterDataSlice.reducer;
export const masterDataActions = masterDataSlice.actions;
